// src/services/CryptoWorkerService.ts

interface EncryptResult {
  data: ArrayBuffer;
  hash: string;
}

type WorkerCommand = 'progressive-encrypt' | 'progressive-decrypt';

/**
 * Il worker risponde con un `notify` allo 0% appena riceve il comando: se entro
 * questo tempo non arriva nulla non è lento, non è vivo. Il timeout copre solo
 * il primo messaggio, non il completamento, che su file grandi può durare a
 * lungo.
 */
const FIRST_MESSAGE_TIMEOUT_MS = 15000;

export class CryptoWorkerService {
  private static instance: CryptoWorkerService;
  private worker: Worker | null = null;
  private workerError: Error | null = null;
  private pendingFailures = new Set<(error: Error) => void>();

  private constructor() {
    if (typeof window === 'undefined') {
      return;
    }

    // Il path segue il basePath dell'app (prod `/app`, staging `/next`, dev
    // nessuno). Derivarlo da NEXT_PUBLIC_API_URL non funzionava: in sviluppo
    // quella variabile vale `http://localhost/`, cioè un'altra origine rispetto
    // a `localhost:3000`, e il worker non veniva mai caricato.
    const basePath = process.env.NEXT_PUBLIC_BASE_PATH
      ? `/${process.env.NEXT_PUBLIC_BASE_PATH}`
      : '';
    const workerPath = `${basePath}/workers/crypto.worker.js`;

    try {
      this.worker = new Worker(workerPath);
    } catch (error: any) {
      this.workerError = new Error(
        `Worker di cifratura non creato (${workerPath}): ${
          error?.message || error
        }`
      );
      return;
    }

    // Un worker che non si carica emette `error` e poi resta muto: senza questo
    // handler ogni postMessage cade nel vuoto, la Promise non si risolve mai e
    // la UI resta in attesa per sempre senza mostrare alcun errore.
    this.worker.addEventListener('error', (event: ErrorEvent) => {
      this.workerError = new Error(
        `Worker di cifratura non disponibile (${workerPath}): ${
          event.message || 'caricamento fallito'
        }`
      );
      this.failPending(this.workerError);
    });
  }

  public static getInstance(): CryptoWorkerService {
    if (!CryptoWorkerService.instance) {
      CryptoWorkerService.instance = new CryptoWorkerService();
    }
    return CryptoWorkerService.instance;
  }

  private failPending(error: Error): void {
    const failures = Array.from(this.pendingFailures);
    this.pendingFailures.clear();
    failures.forEach((fail) => fail(error));
  }

  private run(
    cmd: WorkerCommand,
    params: Record<string, any>,
    transfer: ArrayBuffer
  ): Promise<EncryptResult> {
    if (this.workerError) {
      return Promise.reject(this.workerError);
    }

    const worker = this.worker;
    if (!worker) {
      return Promise.reject(new Error('Worker not initialized'));
    }

    const label = cmd === 'progressive-encrypt' ? 'Encryption' : 'Decryption';

    return new Promise<EncryptResult>((resolve, reject) => {
      let settled = false;
      let handshakeTimer: ReturnType<typeof setTimeout> | undefined;

      const cleanup = () => {
        settled = true;
        worker.removeEventListener('message', handleMessage);
        this.pendingFailures.delete(fail);
        if (handshakeTimer) {
          clearTimeout(handshakeTimer);
          handshakeTimer = undefined;
        }
      };

      const fail = (error: Error) => {
        if (settled) {
          return;
        }
        cleanup();
        reject(error);
      };

      const handleMessage = (e: MessageEvent) => {
        const response = e.data;

        // Il worker ha dato segno di vita: da qui in poi si aspetta solo che
        // finisca, per quanto ci metta.
        if (handshakeTimer) {
          clearTimeout(handshakeTimer);
          handshakeTimer = undefined;
        }

        if (response.status === 1) {
          if (response.type === 'notify') {
            console.debug(`${label} progress: ${response.progress}%`);
          } else if (response.type === 'resolve') {
            cleanup();
            resolve({
              data: response.result,
              hash: response.hash,
            });
          }
        } else {
          cleanup();
          reject(new Error(response.result));
        }
      };

      this.pendingFailures.add(fail);
      worker.addEventListener('message', handleMessage);

      handshakeTimer = setTimeout(
        () =>
          fail(
            new Error(
              `Worker di cifratura non risponde (${cmd}): nessuna risposta entro ${
                FIRST_MESSAGE_TIMEOUT_MS / 1000
              }s`
            )
          ),
        FIRST_MESSAGE_TIMEOUT_MS
      );

      try {
        worker.postMessage({ cmd, params }, [transfer]);
      } catch (e) {
        worker.postMessage({ cmd, params });
      }
    });
  }

  public async encryptFile(
    key: ArrayBuffer,
    data: ArrayBuffer,
    iv: ArrayBuffer
  ): Promise<EncryptResult> {
    return this.run(
      'progressive-encrypt',
      { key, bufferData: data, iv },
      data
    );
  }

  public async decryptFile(
    key: ArrayBuffer,
    data: ArrayBuffer
  ): Promise<EncryptResult> {
    return this.run('progressive-decrypt', { key, bufferData: data }, data);
  }

  public terminate(): void {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
  }
}

export default CryptoWorkerService;
